import React, { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';

const ThreeSteps = () => {
  const { t } = useLanguage();
  const cards = t.threeSteps.cards;
  const trackRef = useRef(null);

  const scrollByCard = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
  };

  return (
    <section id="three-steps" className="relative py-24 md:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16 md:mb-20">
          <p className="dash-title mb-5">{t.threeSteps.eyebrow}</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] light-text leading-[1.25] max-w-3xl mx-auto pb-3">
            {t.threeSteps.title}
          </h2>
        </div>

        {/* Nav — arrows + dots, only worth showing when there's more than one card */}
        {cards.length > 1 && (
          <div className="flex items-center justify-center gap-6 mb-8">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              aria-label="Previous"
              className="w-10 h-10 rounded-full border border-[rgba(232,200,136,0.3)] flex items-center justify-center gold-text hover:bg-[rgba(232,200,136,0.08)] transition-colors"
            >
              ←
            </button>
            <div className="flex gap-2">
              {cards.map((_, i) => (
                <span key={i} className="w-1.5 h-1.5 rounded-full bg-[rgba(232,200,136,0.35)]" />
              ))}
            </div>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              aria-label="Next"
              className="w-10 h-10 rounded-full border border-[rgba(232,200,136,0.3)] flex items-center justify-center gold-text hover:bg-[rgba(232,200,136,0.08)] transition-colors"
            >
              →
            </button>
          </div>
        )}

        {/* Side-scroll carousel — one card fills the viewport at a time */}
        <div
          ref={trackRef}
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          {cards.map((card, ci) => (
            <div key={ci} className="w-full shrink-0 snap-center px-1">
              <div className="rounded-2xl border border-[rgba(232,200,136,0.16)] bg-[rgba(255,255,255,0.02)] px-6 py-10 md:px-12 md:py-14">
                <div className="text-center mb-14 md:mb-16">
                  <h3 className="font-serif text-2xl md:text-3xl light-text leading-snug mb-4">
                    {card.name}
                  </h3>
                  {card.tagline && (
                    <p className="text-[#9CA3AF] text-sm md:text-base leading-relaxed font-light max-w-xl mx-auto">
                      {card.tagline}
                    </p>
                  )}
                </div>

                {/* Same horizontal 3-step layout as the original ThreeSteps design */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-6">
                  {card.steps.map((step, i) => (
                    <div key={i} className="relative flex flex-col items-center text-center px-4">
                      {i < card.steps.length - 1 && (
                        <div
                          className="hidden md:block absolute h-px bg-[rgba(232,200,136,0.18)]"
                          style={{ top: '3rem', left: 'calc(50% + 3.25rem)', right: 'calc(-50% + 3.25rem)' }}
                        />
                      )}
                      <div className="h-24 flex items-center justify-center mb-6">
                        <span className="font-serif text-6xl lg:text-7xl gold-text leading-none">
                          {i + 1}
                        </span>
                      </div>
                      <h4 className="font-serif text-xl lg:text-2xl light-text mb-4 leading-snug">
                        {step.title}
                      </h4>
                      <p className="text-[#9CA3AF] text-sm lg:text-base leading-relaxed font-light max-w-xs">
                        {step.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA de agendamento — centralizado, abaixo da área dos planos */}
        {t.threeSteps.bookingCta && (
          <div className="flex justify-center mt-10">
            <a
              href="https://calendar.app.google/DUXoZugp2zfJufLw9"
              target="_blank"
              rel="noopener noreferrer"
              className="gold-btn inline-block rounded-full px-8 py-4 md:px-10 md:py-4 font-sans text-sm md:text-base font-medium tracking-wide text-center"
            >
              {t.threeSteps.bookingCta}
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default ThreeSteps;
